import { supabase } from "index";
import axios from "axios";
import { BACKEND_URL } from "./constants";
import { Paginated } from "@common/shared/paginated.model";

export const getUserSession = async () => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError) {
    throw sessionError;
  }

  return session;
};

export const apiGetRequest = async <T = any>(path: string, params: Record<string, string> = {}) => {
  try {
    const session = await getUserSession();
    const session_token = session?.access_token;

    const queryParams = new URLSearchParams(params).toString();
    const url = `${BACKEND_URL}${path}?${queryParams}`;

    const { data } = await axios<T>({
      url,
      method: "get",
      headers: {
        "Content-Type": "application/json",
        Authorization: session_token as string,
      },
    });

    return data;
  } catch (err) {
    console.error(err);
    throw err.response.data;
  }
};

export const apiRequest = async <T = any>(
  path: string,
  params: any = {},
  method: "post" | "put" | "patch" = "post"
) => {
  try {
    const session = await getUserSession();
    const session_token = session?.access_token;

    const { data } = await axios<T>({
      url: `${BACKEND_URL}${path}`,
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: session_token as string,
      },
      data: params,
    });

    return data;
  } catch (err) {
    console.error(err);
    throw err.response.data;
  }
};

export async function uploadDocument({
  file,
  loan_id,
  loan_item,
  name,
  draw_request,
  category,
}: {
  file: any;
  loan_id?: string;
  loan_item?: string;
  name?: string;
  draw_request?: string;
  category?: string;
}) {
  const session = await getUserSession();

  const { data, error } = await supabase.storage.from("documents").upload(
    `${session?.user.id}/${new Date().getTime()}_${file.name.replace(
      //prevent key errors in supabase
      /\(|\)|\s|\\./g,
      "_"
    )}`,
    file
  );

  if (!data || error) {
    throw error;
  }

  const { document } = await apiRequest("/supabase/documents/insert", {
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type,
    loan_id,
    loan_item,
    draw_request,
    name,
    path: data.path,
    category,
  });

  return document;
}

export async function uploadStatement({
  file,
  currentStatementId,
}: {
  file: any;
  currentStatementId: string;
}) {
  const session = await getUserSession();

  const { data, error } = await supabase.storage
    .from("statements")
    .upload(`${session?.user.id}/${new Date().getTime()}_${file.name}`, file);

  if (!data || error) {
    throw error;
  }

  const { statement } = await apiRequest("/supabase/statements/update", {
    pdf_statement: data.path,
    currentStatementId,
  });

  return statement;
}

export const api = axios.create({
  baseURL: BACKEND_URL,
});

api.interceptors.request.use(
  async config => {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    config.headers["Content-Type"] = "application/json";

    if (session?.access_token) {
      config.headers.Authorization = session.access_token;
    }

    return config;
  },
  error => Promise.reject(error)
);

export async function fetchResourceList<T>(
  resource: string,
  params: Record<string, string> = {}
): Promise<Paginated<T>> {
  const res = await api.get<Paginated<T>>(`${resource}?${new URLSearchParams(params).toString()}`);
  return res.data;
}

export async function fetchResourceById<T>(resource: string, id: string): Promise<T> {
  const res = await api.get<T>(`${resource}/${id}`);
  return res.data;
}

export async function createResource<T>(resource: string, data: Partial<T>): Promise<T> {
  const res = await api.post<T>(`${resource}`, data);
  return res.data;
}

export function updateResource<T>(resource: string, id: string, data: Partial<T>): Promise<void> {
  return api.put(`${resource}/${id}`, data);
}

export function deleteResource(resource: string, id: string): Promise<void> {
  return api.delete(`${resource}/${id}`);
}
